import { html } from 'lit';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { customElement, property, query, state } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { RemediationActions, RemediationDefaultAction } from '@nirvana/shared/dtos/types/policies/common';
import { styles } from '../view/styles';
import { TextHighlightMixin } from '../../../../../../../../mixins';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { selectAccordionsSearchState } from '@nirvana/redux/searchable-ui-accordions';
import { LabelWidthValues } from '../../../../../../../../types/common/types';
import { FormFieldInput } from '../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { classMap } from 'lit/directives/class-map.js';

export class GzRemediationActionWebComponent extends FormFieldInput<RemediationActions> {
  @property({ type: Object, attribute: true, reflect: true })
  public remediationLabel: { label: string; translatedLabel: string } = {
    label: '',
    translatedLabel: '',
  };

  @property({ type: Object, attribute: true, reflect: true })
  public defaultLabel: { label: string; translatedLabel: string } = {
    label: '',
    translatedLabel: '',
  };

  @property({ type: Array, attribute: true, reflect: true })
  public defaultActionOptions: RemediationDefaultAction[] = [];

  @property({ type: Boolean, attribute: true, reflect: true })
  public isDisabled = false;

  @state()
  private value: RemediationActions;

  @query('#default-action-form-field')
  private defaultActionFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#fallback-action-form-field')
  private fallbackActionFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  /**
   * Template styles.
   */
  static get styles() {
    return styles;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
  }

  protected render() {
    this.updateComplete.then(() => {
      this.defaultActionFormField.value = this.value?.defaultAction ?? '';
    });

    const classes = classMap({
      disabled: this.isDisabled,
    });

    return html` <div>
      <gz-title searchable-label="${this.remediationLabel.label}" fontSize="11" fontWeight="600" class="form-subtitle"
        >${this.remediationLabel.translatedLabel}
      </gz-title>

      <gz-text fontSize="12" fontWeight="400" class="infoText"
        >${translateFn('Select a response to detected threats.')}
      </gz-text>

      <gz-form-field
        id="default-action-form-field"
        searchable-label="${this.defaultLabel.label}"
        label="${this.defaultLabel.translatedLabel}"
        manualValidation
        class="${classes}"
      >
        <gz-dropdown
          .options="${this.defaultActionOptions}"
          gz-form-input
          labelWidth="${LabelWidthValues.LARGE}"
        ></gz-dropdown>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-info-box severity="informative">
        ${this.isDisabled
          ? translateFn('Submission targets and exclusions will be applied as defined in Antimalware > Settings.')
          : translateFn(
              'Submission targets and exclusions will be applied as defined in Antimalware > On-Access Scanning and Antimalware > Settings.'
            )}
      </gz-info-box>
    </div>`;
  }

  getFormValue(): RemediationActions {
    return {
      defaultAction: this.defaultActionFormField.value as RemediationDefaultAction,
    };
  }

  setFormValue(value: RemediationActions): void {
    this.reset();
    this.value = value;
  }

  private reset() {
    this.defaultActionFormField.value = '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-policy-panel-sandbox-remediation-actions': GzRemediationActionWebComponent;
  }
}

/**
 * This wrapper is necessary to connect the web component to the store.
 */
@customElement('gz-policy-panel-sandbox-remediation-actions')
export class GzRemediationActionWrapperWebComponent extends TextHighlightMixin(
  connect(store)(GzRemediationActionWebComponent)
) {
  /**
   * State changed, update search string.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const accordionsSearchApp = selectAccordionsSearchState(rootState);
    this.searchString = accordionsSearchApp.searchString;
  }
}
